import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add as AddIcon } from '@mui/icons-material';
import type { BulletinStampBucket } from '../constants/bulletin';
import {
  BULLETIN_STAMP_META,
  BULLETIN_STAMP_TYPES,
  bulletinStampPublicUrl,
} from '../constants/bulletinStamps';
import type { BulletinStampType } from '../constants/bulletinStamps';

type Props = {
  stampsState: Record<string, BulletinStampBucket>;
  myStampSet: Set<string>;
  busy: boolean;
  onToggle: (type: BulletinStampType) => void;
  onOpenPicker: (anchor: HTMLElement) => void;
  /** スタンプ画像の一辺（px） */
  stampImgSize?: number;
  addButtonSize?: number;
};

/**
 * 掲示板のスタンプ一覧＋追加ボタン（投稿・コメント共通）
 */
const BulletinStampStrip: React.FC<Props> = ({
  stampsState,
  myStampSet,
  busy,
  onToggle,
  onOpenPicker,
  stampImgSize = 20,
  addButtonSize = 36,
}) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
      {BULLETIN_STAMP_TYPES.map((type) => {
        const meta = BULLETIN_STAMP_META[type];
        const bucket = stampsState[type] ?? { count: 0, users: [] };
        if (bucket.count === 0) return null;
        const active = myStampSet.has(type);
        const stampTooltipNames = bucket.users.map((u) => u.display_name).join(', ');
        return (
          <Tooltip key={type} title={stampTooltipNames} placement="top" arrow enterTouchDelay={0}>
            <Box
              component="button"
              type="button"
              disabled={busy}
              onClick={() => void onToggle(type)}
              aria-label={`${meta.label} ${bucket.count}件。クリックで取り消し`}
              aria-pressed={active}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.625,
                py: 0.625,
                px: 1,
                borderRadius: 999,
                border: 'none',
                outline: 'none',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                cursor: busy ? 'default' : 'pointer',
                font: 'inherit',
                color: 'inherit',
                '&:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                },
              }}
            >
              <Box
                component="img"
                src={bulletinStampPublicUrl(meta.imageFile)}
                alt=""
                sx={{ width: stampImgSize, height: stampImgSize, display: 'block', objectFit: 'contain', flexShrink: 0 }}
              />
              <Typography component="span" variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, minWidth: '1em' }}>
                {bucket.count}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
      <Tooltip title="スタンプを追加" placement="top">
        <IconButton
          type="button"
          size="small"
          disabled={busy}
          aria-label="スタンプを追加"
          onClick={(e) => onOpenPicker(e.currentTarget)}
          sx={{
            borderRadius: 999,
            width: addButtonSize,
            height: addButtonSize,
            color: 'text.secondary',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            '&:hover': {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            },
          }}
        >
          <AddIcon sx={{ fontSize: addButtonSize * 0.5 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default BulletinStampStrip;
